import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from 'reactstrap';
import Target from "./Target";
import "./BuiltInTests.scss";
import { 
  calculateAverageDuration, 
  calculateTargetAccuracy, 
  calculateTargetsPerMinute 
} from "./resources/TargetTestCalcs";
import DisplayTargetResults from "./DisplayTargetResults";
import { NUM_PRACTICE_TRIALS, DEFAULT_TARGETS_IN_TEST, DEFAULT_TARGET_SIZE, DEFAULT_TARGET_TYPE, TEST_DRIVE_ACTIVITIES } from "../../Views/ConstForTestDriveSection";

const TargetTest = (props) => {
  const {
    runningPractice,
    disableInput,
    finished,
    setFinished,
    saveActivityResultsToLocalState,
    targetSize = DEFAULT_TARGET_SIZE,
    targetType = DEFAULT_TARGET_TYPE
  } = props;
  let {
    numTrials = DEFAULT_TARGETS_IN_TEST,
    statsToDisplay = [],   //component stats for the test (to display when finished, if you navigate back to the page)
  } = props;

  if(runningPractice)
    numTrials = NUM_PRACTICE_TRIALS;

  let targetClicks = 0;
  const [completedTargets, setCompletedTargets] = useState([]);
  const [started, setStarted] = useState(false);
  const [displayElement, setDisplayElement] = useState(false);
  const [timeTargetAppears, setTimeTargetAppears] = useState(0);
  const targetRef = useRef();

  //whether overall test is completed or not.
  //controlled by parent so that there is "one source of truth" and so parent can handle navigation accordingly

  //component stats for the test (to display when finished, if you navigate back to the page)
  if (completedTargets.length > 0){
    statsToDisplay = completedTargets;
  }

  const handleClick = () => {
    if(disableInput){
      return;
    }
    if (!started) {
      setStarted(true);
    }else if(displayElement){
      const success = targetRef.current && targetRef.current.wasHit();
      ++targetClicks;
      if(success){
        testCompleted();
      }
    }
  }

  //called when a target is hit
  //triggers a call to useEffect() after determining to show next target or end the test
  function testCompleted(){
    setDisplayElement(false);
    
    const targetTime = (Date.now() - timeTargetAppears);
    const totalTargetClicks = targetClicks;

    setCompletedTargets((current) => [
      ...current, {
        duration: targetTime,
        targetEntries: targetRef.current && targetRef.current.getEntries(),
        clicks: totalTargetClicks
      }]
    )
    targetClicks = 0;
  }

  //called on End Activity, to end the test early
  // sets finished to true, which triggers useEffect() to end the test and triggers a re-render with test results
  const handleDone = () => {
    setFinished(true);
  }

  const finishTest = useCallback(() => {
    //just make sure not to call this with empty data
    //(can happen if Continue from test and then Back to see results)
    if(saveActivityResultsToLocalState && completedTargets && completedTargets.length > 0){
      saveActivityResultsToLocalState({
        type: TEST_DRIVE_ACTIVITIES.POINTING_BASIC,
        completedTests: completedTargets, 
        targetsPerMinute: calculateTargetsPerMinute(completedTargets).toFixed(2),
        averageAccuracy: calculateTargetAccuracy(completedTargets).toFixed(2),
        averageDuration: (calculateAverageDuration(completedTargets)/1000).toFixed(2)
      });
    }
  }, [completedTargets, saveActivityResultsToLocalState])

  useEffect(() => {
    if((completedTargets.length >= numTrials) || finished){
      finishTest();
    }else if(started && !displayElement){
        let timeout;
        // Random time between .5 and 1 seconds for target to appear
        const timeToAppear = Math.floor(Math.random() * 500) + 500;
        setTimeTargetAppears(Date.now() + timeToAppear);
  
        timeout = setTimeout(() => {
          targetRef.current && targetRef.current.randomizePosition();
          setDisplayElement(true);
        }, timeToAppear); // Wait for randomized time before the target appears
  
        return () => clearTimeout(timeout);
    }
  }, [displayElement, completedTargets, started, finishTest, finished]);

  return (
    <div>
      {!started && !finished &&
        <div className="aa-target-container" onClick={handleClick}>
          <p><strong>To start, click anywhere in this shaded box</strong></p>
        </div>
      }
      {started && !finished &&
        <div>
          <div id="targetTestContainer" className="aa-target-container" onClick={handleClick}>
            <Target ref={targetRef} targetSize={targetSize} targetType={targetType} hidden={!displayElement} />
          </div>
          <div className="aa-review-row"> 
            <span className="aa-more-info float-start">Target {completedTargets.length + 1} of {numTrials}</span>
            <Button className="aa-end-activity-btn float-end" onClick={handleDone}>End Activity</Button>
          </div>
        </div>
      }
      {finished && (<DisplayTargetResults completedTargets={statsToDisplay}/>)}
    </div>
  );
};

export default TargetTest;